/**
 * Who this client pulls and who it can hear - the "Client rules" of the voice
 * contract, with a Go copy in the server's internal/voice. Change one, change
 * both.
 *
 * Pulling and hearing are deliberately different questions. A speaker whose
 * mic is off is still PULLED: DTX makes a silent track nearly free, and keeping
 * it means their unmute is heard at once instead of after a renegotiation. Only
 * a personal mute or a host mute closes the pull.
 */

/** One player in `voice/{roomId}`.members, as the server writes it. */
export interface VoiceMember {
  /** Has a live mic track. */
  published: boolean;
  /** Bumps on every successful publish; a change means the old track is gone. */
  trackGen: number;
  /** Mic off. Starts true on every join. */
  selfMuted: boolean;
  /** Stamped by the server on every write: `hostMuted(id)` below, against the roster's `hostMute`. */
  hostMuted: boolean;
  joinedAtMs: number;
  /**
   * The server's record of where this member was at their last voice request,
   * for its grace window and for tooling. Clients derive rooms themselves
   * (rooms.ts) and never decide a pull from these: they lag the lobby document.
   */
  room: string;
  prevRoom: string;
  roomChangedAtMs: number;
  observing: boolean;
}

export interface VoiceRoom {
  members: Record<string, VoiceMember>;
}

/**
 * The host's mutes for the whole match. More than a set, because the host can
 * let one player speak while "mute all" stands. The lists persist whether or
 * not a player is in voice, so somebody who joins later arrives muted.
 */
export interface HostMute {
  all: boolean;
  /** Host-muted one at a time. */
  muted: string[];
  /** Exempted from `all`. */
  allowed: string[];
}

/** `voice/{lobbyId}` as a whole: the members, and the host's two switches over them. */
export interface VoiceRoster extends VoiceRoom {
  hostMute: HostMute;
  /** The host turned voice off for this table; the server has already removed everyone. */
  tableOff: boolean;
}

export const NO_HOST_MUTE: HostMute = { all: false, muted: [], allowed: [] };

/**
 * Whether the host has muted `playerId`. A copy of the server's rule, for
 * drawing rows of players who are not in voice - ADVISORY: the server's copy
 * decides what can be published and pulled, and stamps its answer on every
 * member, which is what a member's row and every pull read.
 */
export function hostMuted(hm: HostMute, hostId: string | null, playerId: string): boolean {
  if (playerId === hostId) return false;
  return hm.muted.includes(playerId) || (hm.all && !hm.allowed.includes(playerId));
}

// The host's four moves, as the server applies them. Advisory, like hostMuted.

export function muteAll(hm: HostMute): HostMute {
  return { ...hm, all: true, allowed: [] };
}

/** Lifts every host mute, individual ones too. What a player chose for themselves is not the host's to lift, and is not in here. */
export function unmuteAll(): HostMute {
  return { all: false, muted: [], allowed: [] };
}

export function hostMuteOne(hm: HostMute, playerId: string): HostMute {
  return {
    all: hm.all,
    muted: hm.muted.includes(playerId) ? hm.muted : [...hm.muted, playerId],
    allowed: hm.allowed.filter((id) => id !== playerId),
  };
}

/** Under "mute all" an unmute is an exemption, so letting one person speak to the room does not lift it for the rest. */
export function hostUnmuteOne(hm: HostMute, playerId: string): HostMute {
  return {
    all: hm.all,
    muted: hm.muted.filter((id) => id !== playerId),
    allowed: hm.all && !hm.allowed.includes(playerId) ? [...hm.allowed, playerId] : hm.allowed,
  };
}

export type MuteCheck = (playerId: string) => boolean;

/** Whether `listener` should hold a pull of `speakerId`'s track. */
export function pullable(listener: string, speakerId: string, room: VoiceRoom, isMuted: MuteCheck): boolean {
  if (speakerId === listener) return false;
  const member = room.members[speakerId];
  if (!member) return false;
  return member.published && !member.hostMuted && !isMuted(speakerId);
}

/** Whether `listener` would hear anything from `speakerId` right now. */
export function audible(listener: string, speakerId: string, room: VoiceRoom, isMuted: MuteCheck): boolean {
  return pullable(listener, speakerId, room, isMuted) && !room.members[speakerId]!.selfMuted;
}

/** Every player this client should be pulling, in a stable order. */
export function desiredPulls(room: VoiceRoom, myId: string, isMuted: MuteCheck): string[] {
  return Object.keys(room.members)
    .filter((id) => pullable(myId, id, room, isMuted))
    .sort();
}

export interface PullRecord {
  /** The publisher's trackGen at the time of the pull. */
  trackGen: number;
  /** Our transceiver's mid for it, which is what `voice/close` is addressed by. */
  mid: string;
}

/**
 * What to change to get from the pulls held to the pulls wanted.
 *
 * A member whose trackGen has moved since the pull re-published (a watchdog
 * restart, a re-acquired mic), so the track held is a dead one: it is closed
 * AND pulled again, rather than left to look like a working pull of silence.
 */
export function diffPulls(
  current: ReadonlyMap<string, PullRecord>,
  room: VoiceRoom,
  desired: readonly string[],
): { toPull: string[]; toClose: string[] } {
  const want = new Set(desired);
  const toPull: string[] = [];
  const toClose: string[] = [];
  for (const [playerId, pull] of current) {
    if (!want.has(playerId)) {
      toClose.push(pull.mid);
      continue;
    }
    const member = room.members[playerId];
    if (member && member.trackGen !== pull.trackGen) {
      toClose.push(pull.mid);
      toPull.push(playerId);
    }
  }
  for (const playerId of desired) {
    if (!current.has(playerId)) toPull.push(playerId);
  }
  return { toPull, toClose };
}

/**
 * The roster as the snapshot delivered it, made safe to read.
 *
 * The server is Go, whose nil maps arrive as `null`, and a member written by an
 * older server may lack a field; either would otherwise turn into a TypeError
 * deep in a reconcile. Anything that is not a member-shaped object is dropped.
 */
export function normalizeVoiceRoom(raw: unknown): VoiceRoster {
  const members: Record<string, VoiceMember> = {};
  const doc = raw && typeof raw === "object" ? (raw as { members?: unknown; hostMute?: unknown; tableOff?: unknown }) : null;
  const source = doc?.members;
  if (source && typeof source === "object") {
    for (const [id, value] of Object.entries(source as Record<string, unknown>)) {
      if (!value || typeof value !== "object") continue;
      const m = value as Partial<Record<keyof VoiceMember, unknown>>;
      members[id] = {
        published: m.published === true,
        trackGen: typeof m.trackGen === "number" && Number.isFinite(m.trackGen) ? m.trackGen : 0,
        // Unknown reads as muted: an icon saying somebody's mic is off when it
        // is on is a smaller lie than the reverse.
        selfMuted: m.selfMuted !== false,
        hostMuted: m.hostMuted === true,
        joinedAtMs: typeof m.joinedAtMs === "number" ? m.joinedAtMs : 0,
        room: typeof m.room === "string" ? m.room : "",
        prevRoom: typeof m.prevRoom === "string" ? m.prevRoom : "",
        roomChangedAtMs: typeof m.roomChangedAtMs === "number" ? m.roomChangedAtMs : 0,
        observing: m.observing === true,
      };
    }
  }
  return { members, hostMute: normalizeHostMute(doc?.hostMute), tableOff: doc?.tableOff === true };
}

/** Absent - every roster written before step 8, and every match whose host never touched it - is nobody muted. */
function normalizeHostMute(raw: unknown): HostMute {
  if (!raw || typeof raw !== "object") return NO_HOST_MUTE;
  const hm = raw as { all?: unknown; muted?: unknown; allowed?: unknown };
  // A Go nil slice arrives as null.
  const ids = (v: unknown) => (Array.isArray(v) ? v.filter((id): id is string => typeof id === "string") : []);
  return { all: hm.all === true, muted: ids(hm.muted), allowed: ids(hm.allowed) };
}
